import './WorkspacePage.scss';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import * as Sentry from '@sentry/browser';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { translate } from '~l10n/client';
import { Provider } from 'react-redux';

import { IconButton } from '~shared/components/buttons';
import { IconSidebar } from '~shared/components/icons/standard';
import {
  StyledTab,
  TabList,
  TabPanel,
  TabPanels,
  TabsProvider,
} from '~shared/components/Tabs';
import {
  TeacherApplicationContext,
  TeacherApplicationContextProvider,
} from '~shared/context/TeacherApplicationContext';
import { DrawerProvider, useDrawer } from '~teacher/components/DrawerProvider';
import {
  WorkspaceDeleted,
  WorkspaceMembers,
  WorkspaceQuizzes,
  WorkspaceSettings,
} from '~teacher/components/workspaces';
import { WorkspaceNotFound } from '~teacher/components/workspaces/WorkspaceNotFound/WorkspaceNotFound';
import { WorkspaceSidebar } from '~teacher/components/WorkspaceSidebar';
import {
  RouterProvider,
  TeacherRouter,
  useSocrativeRouter,
} from '~teacher/context/RouterContext';
import {
  fetchWorkspace,
  useWorkspacePermissionsV2,
} from '~teacher/hook/workspaces';
import { queryClient } from '~teacher/queryClient';

import { isAxiosError } from '../../api/isAxiosError';
import { WorkspaceRole } from '../../api/workspaces/getWorkspaceMembers';
import store from '../../store';

interface Props {
  workspaceId: string;
  pageId: WorkspaceTab;
  itemId: string;
}

export const workspaceTabs = [
  'library',
  'deleted',
  'members',
  'settings',
] as const;

export type WorkspaceTab = typeof workspaceTabs[number];

interface UseRedirectNonOwnersFromSettingsParams {
  isOwner: boolean;
  isLoading: boolean;
  pageId: string;
  router: TeacherRouter;
  workspaceId: string;
}

function useRedirectNonOwnersFromSettings({
  isOwner,
  isLoading,
  pageId,
  router,
  workspaceId,
}: UseRedirectNonOwnersFromSettingsParams) {
  React.useEffect(() => {
    if (isLoading) return;
    if (pageId === 'settings' && !isOwner) {
      // not an owner, perform frontend redirect to library
      router.navigate(`workspaces/${workspaceId}/library`, {
        trigger: true,
        replace: true,
      });
    }
  }, [isOwner, isLoading, pageId, router, workspaceId]);
}

export function Workspace({ workspaceId, pageId, itemId }: Props) {
  const [currentItem, setCurrentItem] = React.useState<WorkspaceTab>('library');
  const router = useSocrativeRouter();

  const { workspaceRole, isLoading } = useWorkspacePermissionsV2({
    workspaceId,
  });

  React.useLayoutEffect(() => {
    setCurrentItem(pageId);
  }, [pageId]);

  const { setDrawerOpen } = useDrawer();

  const isOwner = workspaceRole === WorkspaceRole.Owner;

  useRedirectNonOwnersFromSettings({
    isOwner: isOwner,
    isLoading: isLoading,
    pageId: pageId,
    router: router,
    workspaceId: workspaceId,
  });

  return (
    <div className="quizzes-container mx-auto font-sans antialiased flex">
      <WorkspaceSidebar currentWorkspaceId={workspaceId} />
      <div className="flex-1 max-w-full min-w-0 lg:pl-16">
        <TabsProvider
          selectedItem={currentItem}
          id="horizontal"
          onChange={(selectedIndex, selectedItem: WorkspaceTab) => {
            setCurrentItem(selectedItem);
            router.navigate(`workspaces/${workspaceId}/${selectedItem}`, {
              trigger: true,
              replace: false,
            });
          }}
        >
          <div style={{ height: '42px' }} className={'relative'}>
            {isLoading ? (
              // this can be left out and just render nothing until the tabs are ready
              <div
                className={
                  'h-full w-full skeleton-loader absolute top-0 left-0 z-10 rounded-2'
                }
              />
            ) : (
              <></>
            )}
            <TabList label="Test tabs" orientation="horizontal">
              <div
                onClick={(e) => e.stopPropagation()}
                className={
                  'lg:hidden mr-3 relative h-full flex items-center justify-center'
                }
              >
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label={translate('Reveal library menu')}
                >
                  <IconSidebar />
                </IconButton>
              </div>
              <StyledTab item="library">{translate('Quizzes')}</StyledTab>
              <StyledTab item="deleted">{translate('Deleted')}</StyledTab>
              <StyledTab item="members">{translate('Members')}</StyledTab>
              {isOwner ? (
                <StyledTab item="settings">{translate('Settings')}</StyledTab>
              ) : null}
            </TabList>
          </div>
          <TabPanels>
            <TabPanel>
              <></>
            </TabPanel>
            <TabPanel>
              <WorkspaceQuizzes workspaceId={workspaceId} itemId={itemId} />
            </TabPanel>
            <TabPanel>
              <WorkspaceDeleted workspaceId={workspaceId} itemId={itemId} />
            </TabPanel>
            <TabPanel>
              <WorkspaceMembers workspaceId={workspaceId} />
            </TabPanel>
            {isOwner ? (
              <TabPanel>
                <WorkspaceSettings workspaceId={workspaceId} />
              </TabPanel>
            ) : null}
          </TabPanels>
        </TabsProvider>
      </div>
    </div>
  );
}

export default function doWorkSpaces(
  workspaceId: string,
  router: TeacherRouter,
  pageId: WorkspaceTab,
  itemId: string
) {
  if (!workspaceTabs.includes(pageId)) {
    router.navigate(`workspaces/${workspaceId}/library`, {
      trigger: true,
      replace: true,
    });
  }

  fetchWorkspace({ workspaceId, queryClient })
    .then(
      () =>
        Promise.resolve(
          <TeacherApplicationContextProvider
            applicationContext={TeacherApplicationContext.Socrative}
          >
            <QueryClientProvider client={queryClient}>
              <Provider store={store}>
                <ReactQueryDevtools initialIsOpen={false} />
                <DrawerProvider>
                  <RouterProvider router={router}>
                    <Workspace
                      workspaceId={workspaceId}
                      pageId={pageId}
                      itemId={itemId}
                    />
                  </RouterProvider>
                </DrawerProvider>
              </Provider>
            </QueryClientProvider>
          </TeacherApplicationContextProvider>
        ),

      (error: unknown) => {
        if (!isAxiosError(error)) {
          Sentry.captureException(error);
        } else if (error.response.status >= 500) {
          Sentry.captureException(error);
        }
        return Promise.resolve(<WorkspaceNotFound />);
      }
    )
    .then((component) => {
      ReactDOM.render(component, document.getElementById('main-content'));
    });
}
